import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

@Component({
    selector: 'app-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppButtonComponent {
    @Input() type: 'button' | 'submit' | 'reset' = 'button';

    // Prime-style API (so callers can migrate 1:1)
    @Input() label?: string;
    @Input() icon?: string;
    @Input() iconPos?: 'left' | 'right' | 'top' | 'bottom';
    @Input() disabled?: boolean;
    @Input() loading?: boolean;
    @Input() severity?: string;
    @Input() text?: boolean;
    @Input() outlined?: boolean;
    @Input() raised?: boolean;
    @Input() rounded?: boolean;
    @Input() size?: string;
    @Input() badge?: string;
    @Input() badgeClass?: string;
    @Input() styleClass?: string;

    // Accessibility passthroughs
    @Input() ariaLabel?: string;
    @Input() tabIndex?: number;

    get computedButtonClass(): string {
        const cls: string[] = ['p-button', 'p-component', 'app-button'];

        if (this.severity) {
            cls.push(`p-button-${this.severity}`);
        }
        if (this.text) {
            cls.push('p-button-text');
        }
        if (this.outlined) {
            cls.push('p-button-outlined');
        }
        if (this.raised) {
            cls.push('p-button-raised');
        }
        if (this.rounded) {
            cls.push('p-button-rounded');
        }
        if (this.size) {
            cls.push(`p-button-${this.size}`);
        }
        if (this.loading) {
            cls.push('p-disabled', 'p-button-loading');
        }

        const hasLabel = Boolean(this.label && this.label.trim().length > 0);
        const hasIcon = Boolean(this.icon && this.icon.trim().length > 0);
        if (hasIcon && !hasLabel) {
            cls.push('p-button-icon-only');
        }

        if (this.styleClass) {
            cls.push(...this.styleClass.split(' ').filter(Boolean));
        }

        return cls.join(' ');
    }

    get computedAriaLabel(): string | null {
        return this.ariaLabel ?? this.label ?? null;
    }

    get computedDisabled(): boolean {
        return Boolean(this.disabled || this.loading);
    }

    get iconPositionClass(): string {
        // Prime convention: p-button-icon-left/right
        const pos = this.iconPos ?? 'left';
        return pos === 'right' ? 'p-button-icon-right' : 'p-button-icon-left';
    }
}

